import apiClient from "./api";

export interface ProductRelation {
  id?: number;
  name?: string;
}

export interface Attachment {
  id?: number;
  product_id?: number;
  file_name?: string;
  file_path?: string;
  path?: string;
  is_main?: number;
}
export interface StockEntry {
  id?: number;
  product_id?: number;
  warehouse_id?: number;
  warehouse_code?: string;
  warehouse_name?: string;
  qty?: number;
  quantity?: number;
  avg_cost?: number;
  warehouse?: ProductRelation;
}
export interface ProductSize {
  id?: number;
  product_id?: number;
  size_id: number;
  price: number;
  size?: ProductRelation;
}
export interface Product {
  id?: number;
  code: string;
  name: string;
  slug: string;
  cost?: number;
  price?: number;
  qty?: number;
  type?: number;
  show_on_emenu?: boolean | number;
  discount_type?: "fixed" | "percentage" | null;
  discount?: number;
  category_id?: number;
  sub_category_id?: number;
  product_variant_id?: number;
  brand_id?: number;
  unit_id?: number;
  size_id?: number;
  sugar_id?: number;
  tax_id?: number;
  warranty_id?: number;
  created_by?: number;
  updated_by?: number;
  created_at?: string;
  updated_at?: string;
  category?: ProductRelation;
  brand?: ProductRelation;
  unit?: ProductRelation;
  size?: ProductRelation;
  sugar?: ProductRelation;
  attachments?: Attachment[];
  stocks?: StockEntry[];
  product_sizes?: ProductSize[];
}

export interface ProductListResponse {
  data: Product[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

function makeSlug(name: string, code: string): string {
  const n = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  const c = String(code).trim().toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return `${n}-${c}`.replace(/-+/g, "-");
}

const productService = {
  async getProducts(params?: {
    type?: number;
    search?: string;
    q?: string;
    warehouse_id?: number;
    category_id?: number;
    brand_id?: number;
    unit_id?: number;
    size_id?: number;
    pos?: number;
  }): Promise<Product[]> {
    const response = await apiClient.get("/products", { params });
    return response.data.data ?? response.data;
  },

  async getProductsPage(params?: {
    type?: number;
    search?: string;
    q?: string;
    warehouse_id?: number;
    category_id?: number;
    brand_id?: number;
    unit_id?: number;
    size_id?: number;
    pos?: number;
    page?: number;
    per_page?: number | string;
  }): Promise<ProductListResponse> {
    const response = await apiClient.get("/products", { params });
    if (Array.isArray(response.data?.data)) {
      return {
        data: response.data.data,
        current_page: Number(response.data.current_page ?? 1),
        last_page: Number(response.data.last_page ?? 1),
        per_page: Number(response.data.per_page ?? params?.per_page ?? response.data.data.length ?? 10),
        total: Number(response.data.total ?? response.data.data.length ?? 0),
      };
    }

    const list = response.data?.data ?? response.data ?? [];
    return {
      data: Array.isArray(list) ? list : [],
      current_page: 1,
      last_page: 1,
      per_page: Array.isArray(list) ? list.length : 10,
      total: Array.isArray(list) ? list.length : 0,
    };
  },

  async getProduct(id: number): Promise<Product> {
    const response = await apiClient.get(`/products/${id}`);
    return response.data.data ?? response.data;
  },

  async createProduct(input: Omit<Product, "id" | "slug"> | FormData): Promise<Product> {
    let payload;
    if (input instanceof FormData) {
      payload = input;
    } else {
      payload = { ...input, slug: makeSlug(input.name, input.code) };
    }
    const response = await apiClient.post("/products", payload);
    // backend returns { message, data }
    return response.data.data ?? response.data;
  },

  async updateProduct(id: number, input: Partial<Omit<Product, "id">> | FormData): Promise<Product> {
    let payload;
    if (input instanceof FormData) {
      payload = input;
    } else {
      // ensure slug present if name or code provided
      payload = { ...input };
      if ((input.name || input.code) && !input.slug) {
        payload.slug = makeSlug(input.name ?? "", input.code ?? "");
      }
    }

    // For multipart/form-data with PUT/PATCH, we often use POST with _method=PUT
    if (payload instanceof FormData) {
      const response = await apiClient.post(`/products/${id}`, payload);
      return response.data.data ?? response.data;
    } else {
      const response = await apiClient.put(`/products/${id}`, payload);
      return response.data.data ?? response.data;
    }
  },

  async updateEmenuVisibility(id: number, showOnEmenu: boolean): Promise<Product> {
    const response = await apiClient.patch(`/products/${id}/emenu`, {
      show_on_emenu: showOnEmenu,
    });
    return response.data.data ?? response.data;
  },

  async deleteProduct(id: number): Promise<void> {
    await apiClient.delete(`/products/${id}`);
  },

  async exportProducts(format: "excel" | "pdf", ids?: number[]): Promise<Blob> {
    const params: Record<string, any> = { format };
    if (ids && ids.length) {
      params.ids = ids.join(",");
    }
    const response = await apiClient.get("/products/export", {
      params,
      responseType: "blob",
    });
    return response.data as Blob;
  },

  async importProducts(file: File): Promise<{ success: boolean; message?: string }> {
    const form = new FormData();
    form.append("file", file);
    const response = await apiClient.post("/products/import", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data ?? { success: true };
  },
};

export { makeSlug };
export default productService;
